// ABOUTME: Application entry point. Wraps the TanStack Router with AuthProvider.
// ABOUTME: Passes auth state as router context so route guards can check it.

import { RouterProvider } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { router } from "./router";

interface AccountStatusResponse {
  needsAccountSetup: boolean;
}

function isAuthTransitionPath(pathname: string) {
  return (
    pathname === "/reset-password" ||
    pathname === "/setup-account" ||
    pathname === "/activate"
  );
}

function InnerApp() {
  const auth = useAuth();
  const [inviteRedirectChecked, setInviteRedirectChecked] = useState(false);
  const [accountSetupChecked, setAccountSetupChecked] = useState(false);
  // Track which user ID we've already checked to prevent re-runs on token refresh
  const checkedUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // /activate owns its own verification flow (token_hash / OTP code) and
    // must not be redirected away based on auth callback params.
    if (window.location.pathname === "/activate") {
      setInviteRedirectChecked(true);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(
      window.location.hash.replace(/^#/, ""),
    );
    const isSetupFlow =
      window.location.pathname === "/setup-account" ||
      searchParams.get("setup") === "invite";
    const authType = hashParams.get("type") ?? searchParams.get("type");
    const hasAuthCallbackParams =
      hashParams.has("access_token") ||
      searchParams.has("access_token") ||
      hashParams.has("refresh_token") ||
      searchParams.has("refresh_token") ||
      hashParams.has("error_code") ||
      searchParams.has("error_code");

    if (
      (authType === "invite" ||
        authType === "recovery" ||
        hasAuthCallbackParams) &&
      window.location.pathname !==
        (isSetupFlow || authType === "invite"
          ? "/setup-account"
          : "/reset-password")
    ) {
      const targetPath =
        isSetupFlow || authType === "invite"
          ? "/setup-account"
          : "/reset-password";
      const targetUrl = new URL(window.location.href);
      targetUrl.pathname = targetPath;

      if (targetPath === "/setup-account") {
        targetUrl.searchParams.set("setup", "invite");
      }

      window.location.replace(
        `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`,
      );
      return;
    }

    setInviteRedirectChecked(true);
  }, []);

  useEffect(() => {
    const checkAccountStatus = async () => {
      if (isAuthTransitionPath(window.location.pathname)) {
        setAccountSetupChecked(true);
        return;
      }

      if (!auth.user || !auth.session?.access_token) {
        setAccountSetupChecked(true);
        return;
      }

      try {
        const response = await fetch("/api/account/status", {
          headers: {
            Authorization: `Bearer ${auth.session.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`status ${response.status}`);
        }

        const accountStatus = (await response.json()) as AccountStatusResponse;

        if (
          accountStatus.needsAccountSetup &&
          window.location.pathname !== "/setup-account"
        ) {
          window.location.replace("/setup-account");
          return;
        }
      } catch (error) {
        console.error("Failed to load account setup status:", error);
      }

      setAccountSetupChecked(true);
    };

    if (!inviteRedirectChecked || auth.loading) {
      return;
    }

    const userId = auth.user?.id ?? null;

    // Already checked for this user — skip to prevent loop on token refresh
    if (
      checkedUserId.current !== undefined &&
      checkedUserId.current === userId
    ) {
      return;
    }
    // Mark synchronously before async work to prevent concurrent runs
    checkedUserId.current = userId;

    setAccountSetupChecked(false);
    checkAccountStatus();
  }, [
    auth.loading,
    auth.session?.access_token,
    auth.user,
    inviteRedirectChecked,
  ]);

  if (!inviteRedirectChecked || !accountSetupChecked) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (auth.loading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  return <RouterProvider router={router} context={{ auth }} />;
}

export default function App() {
  return (
    <AuthProvider>
      <InnerApp />
    </AuthProvider>
  );
}
